import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

interface PackageData {
  icon: string;
  title: string;
  image: string;
  description: string;
  features: string[];
}

const PACKAGES: PackageData[] = [
  {
    icon: 'ri-heart-2-line',
    title: 'Wedding Venue',
    image: 'https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/4af24bd9-4fc6-4d89-abe7-e71420a4fc3d/4I6A0440.jpg',
    description:
      'Your wedding deserves a venue as extraordinary as your love story. Red Door Ranch provides an elegant, all-inclusive setting with premium bridal suites, grand staircase entrance, lush outdoor ceremony spaces, and the warmth of owners who truly care.',
    features: [
      'Dedicated bridal & groom suites with full bath facilities',
      'Grand staircase for a stunning bridal entrance',
      'Up to 300 guests — indoor & outdoor options',
      'Climate-controlled 6,000+ sq ft main event space',
      'Exclusive use — one event per day guarantee',
      'Multiple staging and layout configurations available',
    ],
  },
  {
    icon: 'ri-goblet-2-line',
    title: 'Private Events',
    image: 'https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/1631653889244-4L86Y1JQ4I8PA1YERL3S/FA606739-06AC-4820-A84F-631482186EA5.jpeg',
    description:
      "Life's milestones deserve a setting that elevates the moment. From quinceañeras and anniversary celebrations to retirement parties and family reunions, Red Door Ranch creates the perfect atmosphere for memorable private events.",
    features: [
      'Flexible indoor and covered outdoor event spaces',
      '3,500 sq ft covered patio area for outdoor gatherings',
      'Beautifully landscaped grounds throughout the property',
      'Vendor-friendly policies for caterers, florists, DJs',
      'Full-day exclusive venue access',
      'Ample on-site parking for all guests',
    ],
  },
  {
    icon: 'ri-briefcase-4-line',
    title: 'Corporate Events',
    image: 'https://images.squarespace-cdn.com/content/v1/5ff52abbfe6e8d5e4781b57a/2e358b63-5ee5-487d-ab0e-aab0fb1485f4/IMG_9809.jpeg',
    description:
      'Elevate your corporate retreat, team-building event, or company celebration beyond the ordinary. Red Door Ranch provides a distinctive setting that inspires connection, fosters creativity, and leaves a lasting impression.',
    features: [
      'Scenic hilltop setting that inspires and energizes',
      'Spacious climate-controlled indoor event hall',
      'Outdoor spaces perfect for group activities',
      'Accommodates up to 300 attendees comfortably',
      'Flexible floor plan configurations',
      'Professional, attentive event support throughout',
    ],
  },
  {
    icon: 'ri-seedling-2-line',
    title: 'Outdoor Ranch Experiences',
    image: 'https://public.readdy.ai/ai/img_res/dc99831f7941556e84d3042959af1b01.jpg',
    description:
      'Immerse yourself in the natural beauty of our 20-acre hilltop estate. From sunset ceremonies amid rolling hills to starlit outdoor receptions, the ranch setting offers a one-of-a-kind backdrop that simply cannot be replicated.',
    features: [
      '20 acres of scenic hilltop property and views',
      'Beautifully landscaped ceremony and reception grounds',
      'Large covered patio areas for outdoor gatherings',
      'Natural ceremony backdrops — no decorating needed',
      'Stunning hilltop views and open-air ambiance',
      'Perfect for engagement photos and elopements',
    ],
  },
];

interface PackageItemProps {
  pkg: PackageData;
  index: number;
}

function PackageItem({ pkg, index }: PackageItemProps) {
  const { ref, isVisible } = useInView({ threshold: 0.1 });
  const isOdd = index % 2 === 1;

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
    >
      {/* Image */}
      <div
        className={`${isOdd ? 'lg:order-2' : ''} ${
          isVisible
            ? isOdd
              ? 'slide-right is-visible'
              : 'slide-left is-visible'
            : isOdd
            ? 'slide-right'
            : 'slide-left'
        }`}
      >
        <div className="relative overflow-hidden w-full h-80 lg:h-[450px] img-hover-zoom">
          <img
            src={pkg.image}
            alt={`${pkg.title} at Red Door Ranch Joshua TX`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute top-6 right-6 w-12 h-12 bg-rdr-dark/60 backdrop-blur-sm flex items-center justify-center">
            <span className="font-serif text-rdr-gold text-lg">{String(index + 1).padStart(2, '0')}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className={`${isOdd ? 'lg:order-1' : ''} ${
          isVisible
            ? isOdd
              ? 'slide-left is-visible delay-200'
              : 'slide-right is-visible delay-200'
            : isOdd
            ? 'slide-left delay-200'
            : 'slide-right delay-200'
        }`}
      >
        <div className="w-9 h-9 flex items-center justify-center border border-rdr-gold/40 bg-rdr-gold/5 mb-6">
          <i className={`${pkg.icon} text-lg text-rdr-gold`} />
        </div>
        <h3 className="font-serif text-rdr-charcoal text-3xl lg:text-4xl mb-5 leading-tight">{pkg.title}</h3>
        <p className="text-rdr-muted font-sans text-base leading-relaxed mb-8 max-w-md">{pkg.description}</p>
        <ul className="space-y-3 mb-10">
          {pkg.features.map((f, j) => (
            <li key={j} className="flex items-start gap-3">
              <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-check-line text-sm text-rdr-gold" />
              </div>
              <span className="text-rdr-muted font-sans text-sm leading-relaxed">{f}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/location#contact"
          className="inline-flex items-center gap-3 bg-rdr-gold text-white font-sans text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-rdr-gold-light transition-colors duration-300 whitespace-nowrap cursor-pointer"
        >
          Book a Tour
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm" />
          </div>
        </Link>
      </div>
    </div>
  );
}

export default function ServicesDetail() {
  const { ref: headRef, isVisible: headVisible } = useInView();

  return (
    <section className="bg-white py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 lg:mb-20 fade-up ${headVisible ? 'is-visible' : ''}`}
        >
          <div className="inline-flex items-center gap-4 mb-5">
            <span className="gold-divider" />
            <p className="text-rdr-gold text-xs tracking-[0.25em] uppercase font-sans">Our Services</p>
            <span className="gold-divider" />
          </div>
          <h2 className="font-serif text-rdr-charcoal text-4xl lg:text-5xl xl:text-6xl max-w-2xl mx-auto leading-tight">
            Thoughtfully Designed for Every Celebration
          </h2>
        </div>

        {/* Packages */}
        <div className="space-y-24 lg:space-y-32">
          {PACKAGES.map((pkg, i) => (
            <PackageItem key={i} pkg={pkg} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
